import { countOptionItems } from '@airship-platform/appointments-feature/utils';
import {
  EmptyState,
  FlexBox,
  Loader,
} from '@airship-platform/jam-web/components';
import { Navigate, Outlet, useParams } from 'react-router';
import { useAppointmentProposalOptionModifiers } from '../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptionProducts } from '../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import { useAppointmentProposals } from '../../data-access/appointmentProposals';
import { useAppointments } from '../../data-access/appointments';
import {
  useAppointmentProposalCosts,
  useAppointmentProposalSync,
} from '../../data-offline';
import ROUTE_PATHS from '../../routes/paths';
import OptionsBuilderHeader from '../proposal-options-buider/components/OptionsBuilderHeader';

const OptionBuilderPage: React.FC = () => {
  const { proposalProducts } = useAppointmentProposalOptionProducts();
  const { proposalModifiers } = useAppointmentProposalOptionModifiers();
  const { options, currentOption, isLoading, error } =
    useAppointmentProposalOptions();
  const { currentProposal } = useAppointmentProposals();
  const { currentAppointment } = useAppointments();
  const { isSyncing: isSyncingProposal } = useAppointmentProposalSync();
  const { isSyncingCosts } = useAppointmentProposalCosts();

  const { appointmentProposalId, appointmentProposalOptionId } = useParams<{
    appointmentProposalId?: string;
    appointmentProposalOptionId?: string;
  }>();

  if (!currentProposal) {
    return (
      <EmptyState
        title="Proposal not found"
        subtitle="Oops! The requested proposal was not found."
      />
    );
  }

  const firstOption = options
    .filter((o) => o.appointmentProposalId === appointmentProposalId)
    .find(Boolean);

  if (!firstOption) {
    return (
      <Navigate
        to={ROUTE_PATHS.PROPOSAL_OPTIONS_PATH({
          appointmentId: currentProposal.appointmentId,
          appointmentProposalId: currentProposal.appointmentProposalId,
          extraPath: 'manage',
        })}
        replace={true}
      />
    );
  }

  if (!appointmentProposalOptionId && firstOption) {
    return (
      <Navigate
        to={ROUTE_PATHS.PROPOSAL_OPTIONS_PATH({
          appointmentId: firstOption.appointmentId,
          appointmentProposalId: firstOption.appointmentProposalId,
          appointmentProposalOptionId: firstOption.appointmentProposalOptionId,
        })}
        replace={true}
      />
    );
  }

  if (appointmentProposalOptionId && error && !currentOption) {
    return <EmptyState title="Option not found" subtitle={error} />;
  }

  const isOptionRouteMatch = !!(
    currentOption &&
    currentOption.appointmentProposalOptionId === appointmentProposalOptionId
  );

  if (appointmentProposalOptionId && !isOptionRouteMatch) {
    return <Loader isLoading message="Loading Option" />;
  }

  return (
    <FlexBox flexDirection={'column'} gap={3}>
      <OptionsBuilderHeader
        customerName={currentAppointment?.customer.name || ''}
        options={options.map((option) => ({
          ...option,
          isSelected:
            option.appointmentProposalOptionId ===
            currentOption?.appointmentProposalOptionId,
          itemCount:
            countOptionItems(
              proposalProducts.filter(
                (p) =>
                  p.appointmentProposalOptionId ===
                  option?.appointmentProposalOptionId
              )
            ) +
            proposalModifiers.filter(
              (m) =>
                m.appointmentProposalOptionId ===
                option?.appointmentProposalOptionId
            ).length,
        }))}
        isViewPresentationButtonDisabled={!proposalProducts.length}
        currentProposal={currentProposal}
        isLoading={isLoading || isSyncingCosts || isSyncingProposal}
      />
      <Outlet />
    </FlexBox>
  );
};

export default OptionBuilderPage;
